import { useEffect, useState } from 'react';
import { useParams } from "react-router-dom";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Paper,
  CircularProgress,
} from '@mui/material';
import { ApiServices } from '../../utils/ApiService';
import { useSnackbar } from 'notistack';

const fieldStyle = { marginBottom: '20px' };

const EditEmployeePage = () => {

  const [roleTab, setRoleTab] = useState([])
  const [branchTab, setBranchTab] = useState([])
  const [isAdmin, setIsAdmin] = useState(false)
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState([]);
  const [isBranchAdmin, setIsBranchAdmin] = useState(false);
  const [isLoginOfficer, setIsLoginOfficer] = useState(false);

  const registerData = {
    name: '',
    gender: '',
    dob: '',
    phoneNo: '',
    email: '',
    address: '',
    branch: '',
    role: '',
  };

  const [data, setData] = useState({ ...registerData });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const res = await ApiServices.get(`/employee/${id}`);
      if (res?.status === 200) {
        setData({
          name: res?.data?.name,
          gender: res?.data?.gender,
          dob: res?.data?.dob,
          phoneNo: res?.data?.phone_no,
          email: res?.data?.email,
          address: res?.data?.address,
          branch: res?.data?.branch,
          role: res?.data?.role
        });

        setIsBranchAdmin(res?.data?.role === Number(2) ? true : false)
        setIsLoginOfficer(res?.data?.role === Number(3) ? true : false)
        if(res?.data?.allowed_branches)
              setItems([...res.data.allowed_branches])
        // else`
        //   setItems([])`
      } else {
        enqueueSnackbar("Failed to fetch data", { variant: "error" });
      }
    } catch (error) {
      const err = error?.response?.data?.message || 'Failed to fetch employee data.';
      console.log(err)
      enqueueSnackbar("Failed to fetch employee data.", { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    setBranchTab(storedUser?.branch_tab || []);
    setRoleTab(storedUser?.role_tab || []);
    setIsAdmin(storedUser?.role === "Super Admin");
  }, []);

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleChange = (e, key) => {
    setData((prev) => ({ ...prev, [key]: e.target.value }));
    if(key === 'role'){
      setIsBranchAdmin(e.target.value === Number(2))
      setIsLoginOfficer(e.target.value === Number(3))
    }
  };

  const handleUpdate = async () => {
    try {
      if(items?.length > 0 && (isBranchAdmin || isLoginOfficer)){
          data["allowedBranches"] = items
      } 
      else{
        data["allowedBranches"] = null;
        setItems([])
      }

      const res = await ApiServices.put(`/employee/${id}`, data);
      if (res?.status === 200) {
        enqueueSnackbar('Employee updated successfully', { variant: 'success' });
      } else {
        enqueueSnackbar(res?.data?.message || 'Update failed.', { variant: 'error' });
      }
    } catch (error) {
      const err = error?.response?.data?.message || 'Update failed.';
      enqueueSnackbar(err, { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', bgcolor: '#f0f2f5', py: 5 }}>
      <Paper elevation={4} sx={{ maxWidth: 600, mx: 'auto', p: 4, borderRadius: 3, background: 'white' }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Edit Employee Details
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Update the fields as necessary. Name cannot be edited.
          </Typography>

          <div style={fieldStyle}>
            <TextField
              label="Full Name"
              fullWidth
              variant="outlined"
              size="medium"
              value={data.name}
              onChange={(e) => handleChange(e, 'name')}
              disabled={!isAdmin}
            />
          </div>

          <div style={fieldStyle}>
            <FormControl fullWidth size="medium">
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select
                labelId="gender-label"
                label="Gender"
                value={data.gender}
                onChange={(e) => handleChange(e, 'gender')}
              >
                <MenuItem value={0}>Male</MenuItem>
                <MenuItem value={1}>Female</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div style={fieldStyle}>
            <TextField
              label="Date of Birth"
              type="date"
              fullWidth
              size="medium"
              InputLabelProps={{ shrink: true }}
              value={data.dob}
              onChange={(e) => handleChange(e, 'dob')}
            />
          </div>

          <div style={fieldStyle}>
            <TextField
              label="Phone Number"
              fullWidth
              size="medium"
              type="tel"
              inputProps={{ maxLength: 10 }}
              value={data.phoneNo}
              onChange={(e) => handleChange(e, 'phoneNo')}
            />
          </div>

          <div style={fieldStyle}>
            <TextField
              label="Email Address"
              fullWidth
              size="medium"
              type="email"
              value={data.email}
              onChange={(e) => handleChange(e, 'email')}
            />
          </div>

          <div style={fieldStyle}>
            <TextField
              label="Address"
              multiline
              rows={3}
              fullWidth
              size="medium"
              value={data.address}
              onChange={(e) => handleChange(e, 'address')}
            />
          </div>

          <div style={fieldStyle}>
            <FormControl fullWidth size="medium">
              <InputLabel id="branch-label">Branch</InputLabel>
              <Select
                labelId="branch-label"
                label="Branch"
                value={data.branch}
                onChange={(e) => handleChange(e, 'branch')}
              >
                {branchTab?.map((item) => (
                  <MenuItem key={item?.id} value={item?.id}>
                    {item?.branch}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div style={fieldStyle}>
            <FormControl fullWidth size="medium">
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                labelId="role-label"
                label="Role"
                value={data.role}
                onChange={(e) => handleChange(e, 'role')}
              >
                {roleTab?.map((item) => (
                  <MenuItem key={item?.id} value={item?.id}>
                    {item?.role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {
            (isBranchAdmin || isLoginOfficer) && (
              <>
              <div style={fieldStyle}>
                <select
                    value=""
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value && !items.includes(value)) {
                        setItems([...items, value]);
                      }
                    }}
                    style={{
                      padding: "12px",
                      width: "100%",
                      borderRadius: "8px",
                      border: "1px solid #ccc"
                    }}
                  >
                    <option value=""> Select Branch</option>

                    {branchTab?.map((option) => (
                      <option key={option?.id} value={option?.branch}>
                        {option?.branch}
                      </option>
                    ))}
                  </select>
              </div>
              <Box mt={1} sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                {items?.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 2,
                      py: 1,
                      background: "#f1f1f1",
                      borderRadius: "8px",
                      fontWeight: "bold",
                      gap: "8px",
                    }}
                  >
                    {item}

                    <span
                      onClick={() => setItems(items?.filter((i) => i !== item))}
                      style={{
                        cursor: "pointer",
                        color: "red",
                        fontWeight: "bold",
                        fontSize: "18px",
                      }}
                    >
                      ×
                    </span>
                  </Box>
                ))}
              </Box> 
              </>
            )
          } 

          <Box mt={3} display="flex" justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 'bold',
                borderRadius: 2,
                background: '#1976d2',
                '&:hover': { background: '#1565c0' },
              }}
              onClick={() => handleUpdate()}
            >
              Update
            </Button>
          </Box>
      </Paper>      
    </Box>
  );
};

export default EditEmployeePage;
